import { Component, computed, signal, inject, OnInit, effect, untracked, OnDestroy, input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    CdkDragDrop,
    DragDropModule,
} from '@angular/cdk/drag-drop';
import {
    applyLayoutEditorAutoWidthUpdates,
    applyLayoutEditorCardDrop,
    computeLayoutEditorAutoWidthUpdates,
    reorderLayoutEditorItemsOnVisibilityToggle,
} from '../../../../shared/components/dashboard/dashboard-layout-editor.helpers';
import { ApiService } from '../../../../core/services/api.service';
import { ThemeService } from '../../../../core/services/theme.service';
import { IndividualToasterComponent } from '../../../../shared/components/loader/loader.component';
import { TableHeader } from '../../../../shared/components/tables/paginated-table/paginated-table.component';
import { ScrollTableComponent } from '../../../../shared/components/tables/scrolled-table/scroll-table.component';
import { IconComponent, IconName } from '../../../../shared/icons/icon.component';
import { DropdownComponent, DropdownOption } from '../../../../shared/components/ui/dropdown/dropdown.component';
import { DatePickerComponent, DateRangePickerOutput } from '../../../../shared/components/date-picker/date-picker.component';
import { environment } from '../../../../../environments/environment';
import { DashboardCardsComponent } from '../../../../shared/components/ui/dashboard-cards/dashboard-cards.component';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Subscription } from 'rxjs';
import { HorizontalBarChartComponent } from '../../../../shared/components/charts/horizontal-bar-chart/horizontal-bar-chart.component';
import { DonutChartComponent } from '../../../../shared/components/charts/donut-chart/donut-chart.component';
import { SingleBarComponent } from '../../../../shared/components/charts/bar-charts/single-bar/single-bar.component';
import { AiHeadlinesComponent } from '../../../../shared/components/ai-headlines/ai-headlines.component';
import {
    DashboardPageKey,
    SeoOverviewSectionId,
    SeoOverviewSectionDef,
} from '../../../../core/enums/dashboard-cards.enum';
import { CustomDashboardPinService } from '../../../../core/services/custom-dashboard-pin.service';
import { DashboardLayoutService } from '../../../../core/services/dashboard-layout.service';
import { CustomizeLayoutBatchService } from '../../../../core/services/customize-layout-batch.service';
import { createEmbeddedSectionFilter, findCatalogPageId, resolveActiveCatalogProductId } from '../../../../shared/components/dashboard/dashboard-embed.helpers';

dayjs.extend(utc);
dayjs.extend(timezone);

// Helper interfaces for aggregation
interface Aggregator {
    clicks: number;
    impressions: number;
    spend: number;
    count: number;
}

/** Persisted layout state per section (visibility + width + order). */
interface SectionLayoutItem {
    id: SeoOverviewSectionId;
    visible: boolean;
    /** 4 | 6 | 8 | 12 — proportional width inside the 12-col dashboard grid. */
    colSpan: number;
    order: number;
}

/** Draft row used by the inline Customize Layout editor. Carries the
 *  section title alongside the persisted layout fields so the popup tile
 *  can render its label without re-looking-up `SECTIONS`. */
interface LayoutEditorItem extends SectionLayoutItem {
    title: string;
}

import { LayoutEditorOverlayDirective } from '../../../../shared/components/dashboard/layout-editor-overlay.directive';
import { IconComponentV2 } from '../../../../shared/components/icon-component/icon.component';

@Component({
    selector: 'app-seo-overview',
    standalone: true,
    imports: [
        CommonModule,
        LayoutEditorOverlayDirective,
        ScrollTableComponent,
        IconComponent,
        DropdownComponent,
        DatePickerComponent,
        DashboardCardsComponent,
        HorizontalBarChartComponent,
        DonutChartComponent,
        SingleBarComponent,
        DragDropModule,
        IndividualToasterComponent,
        AiHeadlinesComponent,
        IconComponentV2,
    ],
    templateUrl: './seo-overview.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styles: [`
        /* Stats Overview: keep the inner cards grid in sync with the parent
           section's col-span (col-4 → 2 per row, col-6 → 3 per row).
           col-8 and col-12 keep their default 4-per-row layout. */
        @media (min-width: 1024px) {
            :host .lg\\:col-span-6 .stats-overview-grid {
                grid-template-columns: repeat(3, minmax(0, 1fr));
            }
            :host .lg\\:col-span-4 .stats-overview-grid {
                grid-template-columns: repeat(2, minmax(0, 1fr));
            }
        }
    `]
})
export class SeoOverviewComponent implements OnInit, OnDestroy {
    private themeService = inject(ThemeService);
    private apiService = inject(ApiService);
    private customDashboardPin = inject(CustomDashboardPinService);
    private dashboardLayoutService = inject(DashboardLayoutService);
    private _customizeLayoutBatch = inject(CustomizeLayoutBatchService);

    readonly mixedMode = input<boolean>(false);
    readonly visibleWidgetKeys = input<string[] | null>(null);
    readonly catalogPageId = input<number | null>(null);
    /**
     * Per-placement state forwarded by the My Custom Dashboard host so the
     * embedded SEO Overview can rehydrate the platform selection that the
     * user picked from the dropdown before pinning. Expected shape:
     *   `{ view: 'metaData' | 'googleAdsSummary' }`
     *
     * Only consumed in `mixedMode` — the standalone page still uses its
     * own `view` dropdown as the source of truth.
     */
    readonly embeddedCustomConfig = input<Record<string, any> | null>(null);

    readonly effectiveCatalogPageId = signal<number | null>(null);

    isDarkTheme = signal(false);
    isLoading = signal(false);

    // View State
    view = 'metaData';

    viewOptions: DropdownOption[] = [
        { label: 'Google Ads', value: 'googleAdsSummary' },
        { label: 'Meta Ads', value: 'metaData' }
    ];

    // Date Picker State
    selectedStartRange: any;
    selectedEndRange: any;

    LA_TIMEZONE = 'America/Los_Angeles';
    nowInLA = () => dayjs().tz(this.LA_TIMEZONE);

    // Cards Data
    adsStats = signal<any>({
        impressions: { value: 0, change: 0 },
        clicks: { value: 0, change: 0 },
        avgCpc: { value: 0, change: 0 },
        spend: { value: 0, change: 0 },
        reach: { value: 0, change: 0 },
        cpm: { value: 0, change: 0 },
        ctr: { value: 0, change: 0 }
    });

    // Table Data Signals
    activeCampaigns = signal<any[]>([]);
    platforms = signal<any[]>([]);

    topCampaign = computed(() => this.activeCampaigns().length > 0 ? this.activeCampaigns()[0] : null);
    topPlatform = computed(() => this.platforms().length > 0 ? this.platforms()[0] : null);
    topCity = computed(() => this.topCities().length > 0 ? this.topCities()[0] : null);
    topCities = signal<any[]>([]);
    devices = signal<any[]>([]);
    genders = signal<any[]>([]);
    ages = signal<any[]>([]);

    // Table Headers
    campaignHeaders: TableHeader[] = [
        { label: 'S No', key: 'index', align: 'text-left' },
        { label: 'Campaign', key: 'campaign' },
        { label: 'Impressions', key: 'impressions' },
        { label: 'Clicks', key: 'clicks' },
        { label: 'CTR', key: 'ctr' }
    ];

    platformHeaders: TableHeader[] = [
        { label: 'S No', key: 'index', align: 'text-left' },
        { label: 'Platform', key: 'platform' },
        { label: 'Clicks', key: 'clicks' }
    ];

    cityHeaders: TableHeader[] = [
        { label: 'S No', key: 'index', align: 'text-left' },
        { label: 'State', key: 'city' },
        { label: 'Clicks', key: 'clicks' },
        { label: 'Cost', key: 'cost', transform: 'currency' }
    ];

    deviceHeaders: TableHeader[] = [
        { label: 'S No', key: 'index', align: 'text-left' },
        { label: 'Device', key: 'device' },
        { label: 'Clicks', key: 'clicks' }
    ];

    genderHeaders: TableHeader[] = [
        { label: 'S No', key: 'index', align: 'text-left' },
        { label: 'Gender', key: 'gender' },
        { label: 'Clicks', key: 'clicks' }
    ];

    ageHeaders: TableHeader[] = [
        { label: 'S No', key: 'index', align: 'text-left' },
        { label: 'Age', key: 'age' },
        { label: 'Clicks', key: 'clicks' }
    ];

    // Computed Configs for Cards
    cardStatsConfig = computed(() => {
        const stats = this.adsStats();
        return [
            {
                label: 'Allocated Budget',
                key: 'spend',
                value: '$' + (stats.spend.value || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
                difference: stats.spend.change || 0,
                icon: 'wallet' as IconName,
                bgClass: 'bg-[#0C66E8]',
                iconClass: 'text-white dark:text-surface-1',
                shadowClass: 'shadow-[0px_4px_12px_0px_#0C66E880]',
            },
            {
                label: 'Reach',
                key: 'reach',
                value: (stats.reach.value || 0).toLocaleString(),
                difference: stats.reach.change || 0,
                icon: 'sparkles' as IconName,
                bgClass: 'bg-[#AD46FF]',
                iconClass: 'text-white dark:text-surface-1',
                shadowClass: 'shadow-[0px_4px_12px_0px_#AD46FF80]',
            },
            {
                label: 'Clicks',
                key: 'clicks',
                value: (stats.clicks.value || 0).toLocaleString(),
                difference: stats.clicks.change || 0,
                icon: 'mousePointer' as IconName,
                bgClass: 'bg-[#FFA514]',
                iconClass: 'text-white dark:text-surface-1',
                shadowClass: 'shadow-[0px_4px_12px_0px_#FFA51480]',
            },
            {
                label: 'Impressions',
                key: 'impressions',
                value: (stats.impressions.value || 0).toLocaleString(),
                difference: stats.impressions.change || 0,
                icon: 'eye' as IconName,
                bgClass: 'bg-[#F05050]',
                iconClass: 'text-white dark:text-surface-1',
                shadowClass: 'shadow-[0px_4px_12px_0px_#F0505080]',
            },
            {
                label: 'Avg. CPC',
                key: 'avgCpc',
                value: '$' + (stats.avgCpc.value || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
                difference: stats.avgCpc.change || 0,
                icon: 'chartColumn' as IconName,
                bgClass: 'bg-[#38385B] dark:bg-white',
                iconClass: 'text-white dark:text-surface-1',
                shadowClass: 'shadow-[0px_4px_12px_0px_#0000001A]',
            },
            {
                label: 'CPM',
                key: 'cpm',
                value: '$' + (stats.cpm.value || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
                difference: stats.cpm.change || 0,
                icon: 'chartPie' as IconName,
                bgClass: 'bg-[#15B268]',
                iconClass: 'text-white dark:text-surface-1',
                shadowClass: 'shadow-[0px_4px_12px_0px_#15B26880]',
            },
            {
                label: 'CTR',
                key: 'ctr',
                value: (stats.ctr.value || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + '%',
                difference: stats.ctr.change || 0,
                icon: 'trendingUp' as IconName,
                bgClass: 'bg-[#F05050]',
                iconClass: 'text-white dark:text-surface-1',
                shadowClass: 'shadow-[0px_4px_12px_0px_#F0505080]',
            }
        ];
    });

    // Computed for Device Chart
    deviceChartData = computed(() => {
        const devices = this.devices();
        const colors: Record<string, { start: string, end: string }> = {
            'Tablet': { start: '#6DA5FF', end: '#2D6FDB' },
            'Desktop': { start: '#2ADFA3', end: '#078B5F' },
            'Mobile Web': { start: '#FFB841', end: '#BC7600' },
            'Mobile App': { start: '#A983FF', end: '#7946EB' }
        };

        return devices.map(d => ({
            label: d.device,
            value: d.clicks,
            gradientStart: colors[d.device]?.start,
            gradientEnd: colors[d.device]?.end,
            color: colors[d.device]?.end // fallback
        }));
    });

    // Computed for Gender Chart
    genderChartData = computed(() => {
        const genders = this.genders();
        const colors: Record<string, string> = {
            'Male': '#0C66E8',
            'Female': '#AD46FF',
            'Unknown': '#BFBFC7'
        };

        return genders.map(g => ({
            label: g.gender,
            value: g.clicks,
            color: colors[g.gender] || '#C1C1D1'
        }));
    });

    // Computed for Gender Stats Summary
    genderStatsSummary = computed(() => {
        const genders = this.genders();
        const totalClicks = genders.reduce((sum, g) => sum + g.clicks, 0);

        return genders.map(g => ({
            label: g.gender,
            clicks: g.clicks,
            percentage: totalClicks > 0 ? Math.round((g.clicks / totalClicks) * 100) : 0,
            color: g.gender === 'Male' ? 'text-[#0C66E8]' : g.gender === 'Female' ? 'text-[#AD46FF]' : 'text-[#7D7D93]'
        }));
    });

    // Computed for Age Chart
    ageChartData = computed(() => {
        const ages = this.ages();
        // The ages labels might come from API in uppercase like 'AGE_RANGE_25_34' 
        // but they are already processed in loadAdsStats to '25-34' etc.
        return ages.map(a => ({
            label: a.age,
            value: a.clicks,
            start: '',
            end: ''
        }));
    });

    hasStatsData = computed(() => {
        const stats = this.adsStats();
        // Check if any major metric has data
        return stats.impressions.value > 0 ||
            stats.clicks.value > 0 ||
            stats.spend.value > 0 ||
            this.activeCampaigns().length > 0;
    });

    private lastPayload: string = '';
    private statsSubscription: Subscription | null = null;
    /** Tags in-flight fetches so stale responses are ignored after product changes. */
    private reportFetchGeneration = 0;

    constructor() {
        this.isDarkTheme.set(this.themeService.getTheme() === 'dark');

        effect(() => {
            const productId = this.apiService.selectedProductId();
            if (productId) {
                untracked(() => {
                    this.reportFetchGeneration += 1;
                    this.loadAdsStats();
                });
            }
        });

        // ── Hydrate the platform dropdown from the per-placement `custom_config`
        // when this component is rendered as an embedded card on My Custom
        // Dashboard. The widget-host pushes the saved
        // `{ view: 'metaData' | 'googleAdsSummary' }` payload here so the
        // pinned card renders the same Meta/Google ads data the user was
        // looking at when they hit "Pin to Custom Dashboard". On the
        // standalone page `mixedMode()` is false and this effect no-ops,
        // so the user's dropdown selection remains the source of truth.
        effect(() => {
            if (!this.mixedMode()) return;
            const config = this.embeddedCustomConfig();
            const savedView = config?.['view'];
            if (savedView !== 'metaData' && savedView !== 'googleAdsSummary') return;
            if (this.view === savedView) return;
            this.view = savedView;
            untracked(() => {
                // Reset the `lastPayload` de-dup so a pending/completed
                // `loadAdsStats()` call with the old `view` doesn't block
                // the new platform from being requested.
                this.lastPayload = '';
                this.loadAdsStats();
            });
        });

        effect(() => {
            const fromParent = this.catalogPageId();
            if (fromParent != null) {
                untracked(() => this.effectiveCatalogPageId.set(fromParent));
                return;
            }
            const productId = this.apiService.selectedProductId();
            if (!productId) return;
            untracked(() => {
                this.dashboardLayoutService.getCatalogProducts().subscribe((catalog) => {
                    const activeProductPid = resolveActiveCatalogProductId(this.apiService);
                    const pageId = findCatalogPageId(catalog as any[], this.PAGE_KEY, activeProductPid);
                    if (pageId != null) this.effectiveCatalogPageId.set(pageId);
                });
            });
        });

        effect(() => {
            if (this.mixedMode()) return;
            const pageId = this.effectiveCatalogPageId();
            if (pageId == null) return;
            untracked(() => {
                this.customDashboardPin.hydratePinnedWidgetKeys(pageId).subscribe((keys) => {
                    const pinned = new Set<SeoOverviewSectionId>();
                    keys.forEach((k) => {
                        if (Object.values(SeoOverviewSectionId).includes(k as SeoOverviewSectionId)) {
                            pinned.add(k as SeoOverviewSectionId);
                        }
                    });
                    this.pinnedSections.set(pinned);
                });
            });
        });
    }

    ngOnInit(): void {
        // Hydrate layout (visibility / colSpan / order) and pinned-section
        // state from localStorage so the page can support per-card resize +
        // drag-drop reordering across reloads.
        this._initLayout();
    }

    ngOnDestroy(): void {
        if (this.statsSubscription) {
            this.statsSubscription.unsubscribe();
        }
    }

    // ─── Section catalog ────────────────────────────────────────────────────
    readonly PAGE_KEY = DashboardPageKey.SEO_OVERVIEW;
    readonly SectionId = SeoOverviewSectionId;
    readonly SECTIONS = SeoOverviewSectionDef;

    // ─── Section layout state (visibility + colSpan + order) ────────────────
    /** Persisted layout map driving the actual rendered page. */
    readonly layoutMap = signal<Record<SeoOverviewSectionId, SectionLayoutItem>>(
        {} as Record<SeoOverviewSectionId, SectionLayoutItem>,
    );

    /** All sections sorted by their persisted order. */
    readonly orderedSections = computed(() =>
        Object.values(this.layoutMap()).slice().sort((a, b) => a.order - b.order),
    );

    /** Sections currently visible, in order — used by the live grid. */
    readonly orderedVisibleSections = computed(() =>
        this.orderedSections().filter((s) => s.visible),
    );

    readonly embeddedVisibleSections = createEmbeddedSectionFilter(
        this.orderedVisibleSections,
        this.mixedMode,
        this.visibleWidgetKeys,
        this.orderedSections,
    );

    isVisible(sectionId: SeoOverviewSectionId): boolean {
        return this.layoutMap()[sectionId]?.visible ?? true;
    }

    /** Tailwind col-span class for a section in the live 12-col grid. */
    getSectionColClass(item: SectionLayoutItem | SeoOverviewSectionId): string {
        const id = typeof item === 'string' ? item : item.id;
        const span = this._normalizeColSpan(this.layoutMap()[id]?.colSpan ?? 12);
        switch (span) {
            case 4: return 'col-span-12 lg:col-span-4';
            case 6: return 'col-span-12 lg:col-span-6';
            case 8: return 'col-span-12 lg:col-span-8';
            case 12: default: return 'col-span-12';
        }
    }

    trackSection = (_idx: number, sec: SectionLayoutItem): string => sec.id;

    // ─── "Pin to Custom Dashboard" (API-backed) ─────────────────────────────
    readonly pinnedSections = signal<Set<SeoOverviewSectionId>>(new Set());
    readonly pendingPinSections = signal<Set<SeoOverviewSectionId>>(new Set());

    isAddedToCustomDashboard(sectionId: SeoOverviewSectionId): boolean {
        return this.pinnedSections().has(sectionId);
    }

    isAddingToCustomDashboard(sectionId: SeoOverviewSectionId): boolean {
        return this.pendingPinSections().has(sectionId);
    }

    addToCustomDashboard(
        sec: { id: SeoOverviewSectionId } | SeoOverviewSectionId,
    ): void {
        // Accept either the full section layout item (templates iterating
        // over `orderedSections()` pass `sec`) or the raw section id
        // (templates fanning out via `<ng-template let-sectionId>`). Both
        // flow into the same `CustomDashboardPinService.toggleSection(...)`
        // PATCH request below.
        const sectionId =
            typeof sec === 'object' && sec !== null ? sec.id : sec;
        if (this.pendingPinSections().has(sectionId)) return;
        const pageId = this.effectiveCatalogPageId();
        if (pageId == null) return;

        const colSpan = this.layoutMap()[sectionId]?.colSpan ?? 12;
        this.customDashboardPin.toggleSection({
            pageId,
            widgetKey: sectionId,
            colSpan,
            // Persist the currently-selected platform alongside the placement
            // so the embedded copy on My Custom Dashboard renders the same
            // Meta or Google Ads data the user is looking at right now.
            // Without this, the pinned card silently re-mounts with the
            // standalone default (`metaData`) regardless of dropdown choice.
            customConfig: { view: this.view },
            isCurrentlyPinned: this.isAddedToCustomDashboard(sectionId),
            onPinnedChange: (pinned) => {
                this.pinnedSections.update((s) => {
                    const next = new Set(s);
                    if (pinned) next.add(sectionId);
                    else next.delete(sectionId);
                    return next;
                });
            },
            onPendingChange: (pending) => {
                this.pendingPinSections.update((s) => {
                    const next = new Set(s);
                    if (pending) next.add(sectionId);
                    else next.delete(sectionId);
                    return next;
                });
            },
        });
    }

    // ─── Customize Layout editor (mini-dashboard preview) ───────────────────
    /** State for the inline Customize Layout editor. */
    readonly isLayoutEditorOpen = signal(false);
    readonly editorItems = signal<LayoutEditorItem[]>([]);

    /** Number of cards currently marked visible in the editor draft. */
    readonly editorVisibleCount = computed(
        () => this.editorItems().filter((i) => i.visible).length,
    );

    trackEditorItem = (_idx: number, item: LayoutEditorItem): string => item.id;

    /** Allowed col-span steps the user can cycle through per card. */
    private readonly COL_SPAN_STEPS: ReadonlyArray<number> = [4, 6, 8, 12];

    /** localStorage key for this page's persisted layout. */
    private readonly LAYOUT_LS_KEY = 'prism_seo_overview_layout_v1';

    /** Default col-span per section — chosen so the grid matches the
     *  historical 3fr/2fr split (closest steps: 8 + 4) with stats overview
     *  at full width. */
    private readonly DEFAULT_COL_SPAN: Record<SeoOverviewSectionId, number> = {
        [SeoOverviewSectionId.STATS_OVERVIEW]: 12,
        [SeoOverviewSectionId.ACTIVE_CAMPAIGNS]: 8,
        [SeoOverviewSectionId.PLATFORMS]: 4,
        [SeoOverviewSectionId.DEVICE]: 8,
        [SeoOverviewSectionId.GENDER]: 4,
        [SeoOverviewSectionId.TOP_CITIES]: 8,
        [SeoOverviewSectionId.AGE]: 4,
    };

    openCustomizeLayout(): void {
        const titleById = new Map(this.SECTIONS.map((s) => [s.id, s.title]));
        const items: LayoutEditorItem[] = this.orderedSections().map((s) => ({
            id: s.id,
            title: titleById.get(s.id) ?? s.id,
            visible: s.visible,
            colSpan: this._normalizeColSpan(s.colSpan),
            order: s.order,
        }));
        this.editorItems.set(items);
        this.isLayoutEditorOpen.set(true);
    }

    closeLayoutEditor(): void {
        this.isLayoutEditorOpen.set(false);
    }

    toggleEditorCardVisibility(id: SeoOverviewSectionId): void {
        // When a card is hidden, push it to the bottom so the visible cards
        // reflow into the freed grid slot. When shown again, slot it back in
        // just above the first hidden item so it rejoins the visible group.
        this.editorItems.update((items) =>
            reorderLayoutEditorItemsOnVisibilityToggle(items, id),
        );
    }

    /** Restores the editor draft to the page's default order, widths, and visibility. */
    resetLayoutEditorToDefaults(): void {
        const defaults: LayoutEditorItem[] = this.SECTIONS.map((s, idx) => ({
            id: s.id as SeoOverviewSectionId,
            title: s.title,
            visible: true,
            colSpan: this._normalizeColSpan(
                this.DEFAULT_COL_SPAN[s.id as SeoOverviewSectionId] ?? 12,
            ),
            order: idx,
        }));
        this.editorItems.set(defaults);
    }

    /** Persists the editor draft to local layout state + localStorage. */
    applyLayoutEditor(): void {
        const draft = this.editorItems();
        const layout: Record<SeoOverviewSectionId, SectionLayoutItem> =
            {} as Record<SeoOverviewSectionId, SectionLayoutItem>;
        draft.forEach((d, idx) => {
            layout[d.id] = {
                id: d.id,
                visible: d.visible,
                colSpan: this._normalizeColSpan(d.colSpan),
                order: idx,
            };
        });
        this.layoutMap.set(layout);
        this._saveLayoutToStorage(layout);

        // Best-effort backend persistence via the shared
        // `CustomizeLayoutBatchService` — same flow as Social Platform.
        // Service no-ops silently when `pageId` / catalog isn't ready or
        // the user has no mixed dashboard yet, so existing local-only
        // behaviour is preserved.
        const pageId = this.effectiveCatalogPageId();
        if (pageId != null) {
            this._customizeLayoutBatch.applyEditorDraftToPageDashboardByKeys({
                pageId,
                productId: resolveActiveCatalogProductId(this.apiService),
                fallbackSourcePageId: pageId,
                draft: draft.map((d, idx) => ({
                    key: String(d.id),
                    sourcePageId: pageId,
                    colSpan: this._normalizeColSpan(d.colSpan),
                    visible: d.visible,
                    customConfig: null,
                    order: idx,
                })),
            });
        }

        this.isLayoutEditorOpen.set(false);
    }

    // ─── Editor: width chips (decrease / increase / labels) ─────────────────
    /** Steps a card down one width. Respects each card's designed default as the lower bound. */
    decreaseEditorCardColSpan(id: SeoOverviewSectionId): void {
        this.editorItems.update((items) =>
            items.map((i) => {
                if (i.id !== id) return i;
                const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
                if (idx <= 0) return i;
                const nextSpan = this.COL_SPAN_STEPS[idx - 1];
                if (this._isBelowDefaultColSpan(i.id, nextSpan)) return i;
                return { ...i, colSpan: nextSpan };
            }),
        );
    }

    increaseEditorCardColSpan(id: SeoOverviewSectionId): void {
        this.editorItems.update((items) =>
            items.map((i) => {
                if (i.id !== id) return i;
                const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
                if (idx < 0 || idx >= this.COL_SPAN_STEPS.length - 1) return i;
                return { ...i, colSpan: this.COL_SPAN_STEPS[idx + 1] };
            }),
        );
    }

    /**
     * Auto-adjusts widths in the editor so every row fully spans the 12-col
     * grid. Delegates to the shared {@link computeLayoutEditorAutoWidthUpdates}
     * helper, which grows the smallest card in each leaky row (and shares the
     * leftover equally when multiple cards tie at the smallest width).
     */
    autoAdjustEditorWidths(): void {
        const updates = computeLayoutEditorAutoWidthUpdates(this.editorItems());
        if (!updates.size) return;
        this.editorItems.update((items) =>
            applyLayoutEditorAutoWidthUpdates(items, updates),
        );
    }

    /** Whether clicking Auto Adjust Width would actually grow at least one card. */
    canAutoAdjustEditorWidths(): boolean {
        return computeLayoutEditorAutoWidthUpdates(this.editorItems()).size > 0;
    }

    /**
     * Whether the minimize button should be active for this card. A card
     * cannot shrink below its designed default col-span — e.g. a card
     * designed for `col-8` can only step between 8 and 12.
     */
    canDecreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
        if (idx <= 0) return false;
        if (this._isBelowDefaultColSpan(item.id, this.COL_SPAN_STEPS[idx - 1])) {
            return false;
        }
        return true;
    }

    /** Whether `span` is below the designed default col-span for `id`. */
    private _isBelowDefaultColSpan(
        id: SeoOverviewSectionId,
        span: number,
    ): boolean {
        const defaultSpan = this.DEFAULT_COL_SPAN[id];
        if (defaultSpan == null) return false;
        return span < this._normalizeColSpan(defaultSpan);
    }

    canIncreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
        return idx >= 0 && idx < this.COL_SPAN_STEPS.length - 1;
    }

    /** Tailwind col-span class for an editor tile inside the popup's 12-col grid. */
    getEditorColSpanClass(span: number): string {
        switch (this._normalizeColSpan(span)) {
            case 4: return 'col-span-4';
            case 6: return 'col-span-6';
            case 8: return 'col-span-8';
            case 12: return 'col-span-12';
            default: return 'col-span-12';
        }
    }

    /** Human-friendly label for a col-span value (Small / Medium / Large / Full). */
    getEditorColSpanLabel(span: number): string {
        switch (this._normalizeColSpan(span)) {
            case 4: return 'Small';
            case 6: return 'Medium';
            case 8: return 'Large';
            case 12: return 'Full';
            default: return `${span}`;
        }
    }
    /* Swaps the dragged card with the card at the drop target so that ONLY those two tiles exchange positions in the array; every other card keeps its current array slot (no shuffle/cascade like CDK's default `moveItemInArray`). we changed the strategy from shift to pairwise-swap [event, { strategy: 'shift' });]*/
    onEditorCardDrop(event: CdkDragDrop<LayoutEditorItem[]>): void {
        const next = applyLayoutEditorCardDrop(this.editorItems(), event, {
            strategy: 'pairwise-swap',
        });
        if (next) this.editorItems.set(next);
    }
    // ─── Layout state private helpers ───────────────────────────────────────
    private _initLayout(): void {
        const stored = this._loadLayoutFromStorage();
        const layout: Record<SeoOverviewSectionId, SectionLayoutItem> =
            {} as Record<SeoOverviewSectionId, SectionLayoutItem>;

        this.SECTIONS.forEach((s, idx) => {
            const sid = s.id as SeoOverviewSectionId;
            const storedItem = stored?.[sid];
            layout[sid] = {
                id: sid,
                visible: storedItem?.visible ?? true,
                colSpan: this._normalizeColSpan(
                    storedItem?.colSpan ?? this.DEFAULT_COL_SPAN[sid] ?? 12,
                ),
                order: storedItem?.order ?? idx,
            };
        });

        this.layoutMap.set(layout);
    }

    private _loadLayoutFromStorage(): Record<string, SectionLayoutItem> | null {
        try {
            const raw = localStorage.getItem(this.LAYOUT_LS_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    }

    private _saveLayoutToStorage(
        layout: Record<SeoOverviewSectionId, SectionLayoutItem>,
    ): void {
        try {
            localStorage.setItem(this.LAYOUT_LS_KEY, JSON.stringify(layout));
        } catch {
            // localStorage unavailable (e.g. private mode) — silently ignore.
        }
    }

    private _normalizeColSpan(span: number | null | undefined): number {
        const allowed = [4, 6, 8, 12];
        if (span == null || !Number.isFinite(span)) return 12;
        if (allowed.includes(span)) return span;
        if (span <= 4) return 4;
        if (span <= 6) return 6;
        if (span <= 8) return 8;
        return 12;
    }

    setActiveTab(optionOrValue: any) {
        const value = typeof optionOrValue === 'object' && optionOrValue !== null && 'value' in optionOrValue
            ? optionOrValue.value
            : optionOrValue;

        this.view = value as string;
        this.loadAdsStats();
    }

    onDateRangeFilter(event: DateRangePickerOutput) {
        const { start_date, end_date } = event;
        if (start_date && end_date) {
            this.selectedStartRange = dayjs.utc(start_date).tz(this.LA_TIMEZONE);
            this.selectedEndRange = dayjs.utc(end_date).tz(this.LA_TIMEZONE);
            this.loadAdsStats();
        }
    }

    loadAdsStats() {
        const productId = this.apiService.getSelectedProductId() || sessionStorage.getItem('selected_product_id');
        if (!productId || !this.selectedStartRange || !this.selectedEndRange) return;

        const fetchGeneration = this.reportFetchGeneration;
        const fetchProductId = productId;

        const stored = sessionStorage.getItem('dynamic_routes');
        const dynamicRoutes = stored ? JSON.parse(stored) : [];
        const selectedProduct = dynamicRoutes.find((p: any) => p.product?.id === productId);
        const rawProductName = selectedProduct?.product?.name || '';

        const productMapping: Record<string, string> = {
            'google': 'GOOGLE',
            'meta': 'META',
            'practina': 'PRACTINA',
            'credee': 'CREDEE',
            'cool credit': 'COOL_CREDIT',
            'lendee': 'LENDEE',
            'finance mutual': 'FINANCE_MUTUAL',
            'denefits': 'DENEFITS',
            'recuvery': 'RECUVERY'
        };

        const productPayload = productMapping[rawProductName.toLowerCase()] || rawProductName.toUpperCase();
        // Determine platform based on the current view
        const platformPayload = this.view === 'metaData' ? 'META' : 'GOOGLE';

        const payload = {
            "start_date": dayjs(this.selectedStartRange).format('YYYY-MM-DD'),
            "end_date": dayjs(this.selectedEndRange).format('YYYY-MM-DD'),
            "product": productPayload,
            "platform": platformPayload
        };

        const currentPayloadStr = JSON.stringify(payload);
        if (this.lastPayload === currentPayloadStr) {
            return;
        }
        this.lastPayload = currentPayloadStr;

        const headers = {
            'Access-Control-Allow-Origin': '*',
            'Access-Control-Allow-Methods': 'GET, POST, OPTIONS, PUT, PATCH, DELETE',
            'Access-Control-Allow-Headers': 'X-Requested-With,content-type'
        };

        const practinaServer = environment.productServerUrls.find(server => server.name === 'practina');
        const apiUrl = practinaServer ? `${practinaServer.url}v1/api/ads/stats` : '';

        if (this.statsSubscription) {
            this.statsSubscription.unsubscribe();
        }

        this.isLoading.set(true);
        this.statsSubscription = this.apiService.post(apiUrl, payload, { headers, withCredentials: false, showLoader: false }).subscribe({
            next: (response: any) => {
                const currentProductId =
                    this.apiService.getSelectedProductId() || sessionStorage.getItem('selected_product_id');
                const isStale = fetchGeneration !== this.reportFetchGeneration
                    || fetchProductId !== currentProductId;
                if (isStale) {
                    this.isLoading.set(false);
                    return;
                }
                this.isLoading.set(false);
                if (response) {
                    this.processConsolidatedResponse(response, rawProductName);
                }
            },
            error: (err: any) => {
                this.isLoading.set(false);
                console.error('Error loading ads stats:', err);
                this.resetSignals();
            }
        });
    }

    private processConsolidatedResponse(response: any, rawProductName: string) {
        const dataRoot = response.DATA || response.data || (response.SUCCESS === true ? response : response);

        const findKey = (obj: any, target: string) => obj ? Object.keys(obj).find(k => k.toLowerCase() === target.toLowerCase()) : null;

        let productKey = findKey(dataRoot, rawProductName);
        let productData = productKey ? dataRoot[productKey] : null;

        // Fallback checks for META or GOOGLE keys depending on current view context
        if (!productData) {
            if (this.view === 'metaData' && dataRoot.META) {
                productKey = findKey(dataRoot.META, rawProductName);
                productData = productKey ? dataRoot.META[productKey] : null;
            } else if (this.view === 'googleAdsSummary' && dataRoot.GOOGLE) {
                productKey = findKey(dataRoot.GOOGLE, rawProductName);
                productData = productKey ? dataRoot.GOOGLE[productKey] : null;
            }
        }

        if (!productData) {
            // Last ditch effort fallback
            const fallbackKey = this.view === 'metaData' ? 'META' : 'GOOGLE';
            if (dataRoot[fallbackKey]) {
                productKey = findKey(dataRoot[fallbackKey], rawProductName);
                productData = productKey ? dataRoot[fallbackKey][productKey] : null;
            }
        }

        if (!productData) {
            console.warn('No product data found for:', rawProductName);
            this.resetSignals();
            return;
        }

        // Initialize Aggregators
        const campaignsMap = new Map<string, any>();
        const citiesMap = new Map<string, Aggregator>();
        const devicesMap = new Map<string, Aggregator>();
        const gendersMap = new Map<string, Aggregator>();
        const agesMap = new Map<string, Aggregator>();
        const platformsMap = new Map<string, Aggregator>();

        const globalStats = { clicks: 0, impressions: 0, spend: 0, reach: 0, cpm: 0, ctr: 0, cpc: 0 };

        const campaignsContainer = productData.campaigns || {};

        Object.values(campaignsContainer).forEach((campaign: any) => {
            const campName = campaign.name || 'Unknown Campaign';

            if (!campaignsMap.has(campName)) {
                campaignsMap.set(campName, { id: campaign.id, name: campName, clicks: 0, impressions: 0, spend: 0 });
            }
            const campStats = campaignsMap.get(campName);

            const statsContainer = campaign.stats || {};

            Object.keys(statsContainer).forEach(dateKey => {
                // FIXED: Regex now accepts YYYY-MM (e.g. 2026-01) OR YYYY-MM-DD
                if (!/^\d{4}-\d{2}(-\d{2})?$/.test(dateKey)) return;

                const dayData = statsContainer[dateKey];
                const overall = dayData.overall || dayData;

                const clicks = Number(overall.clicks || 0);
                const impressions = Number(overall.impressions || 0);
                const spend = Number(overall.spend || 0);
                const reach = Number(overall.reach || 0);

                // Aggregate Global
                globalStats.clicks += clicks;
                globalStats.impressions += impressions;
                globalStats.spend += spend;
                globalStats.reach += reach;

                // Aggregate Campaign
                campStats.clicks += clicks;
                campStats.impressions += impressions;
                campStats.spend += spend;

                // --- Helper for breakdown aggregation ---
                const aggregateToMap = (sourceObj: any, targetMap: Map<string, Aggregator>, keyFormatter?: (k: string) => string) => {
                    if (!sourceObj) return;
                    Object.entries(sourceObj).forEach(([key, val]: [string, any]) => {
                        const cleanKey = keyFormatter ? keyFormatter(key) : key;
                        if (!targetMap.has(cleanKey)) {
                            targetMap.set(cleanKey, { clicks: 0, impressions: 0, spend: 0, count: 0 });
                        }
                        const entry = targetMap.get(cleanKey)!;
                        entry.clicks += Number(val.clicks || 0);
                        entry.impressions += Number(val.impressions || 0);
                        entry.spend += Number(val.spend || 0);
                    });
                };

                // Aggregations
                aggregateToMap(dayData.by_region, citiesMap, (k) => k.includes('|') ? k.split('|')[1] : k);
                aggregateToMap(dayData.by_device, devicesMap, (k) => k.replace(/_/g, ' '));
                aggregateToMap(dayData.by_gender, gendersMap, (k) => k.charAt(0).toUpperCase() + k.slice(1).toLowerCase());
                aggregateToMap(dayData.by_age, agesMap, (k) => {
                    if (k === 'UNDETERMINED') return 'Undetermined';
                    return k.replace('AGE_RANGE_', '').replace(/_/g, '-');
                });
                aggregateToMap(dayData.by_platform, platformsMap);
            });
        });

        // --- Transform to Arrays & Sort ---
        const toSortedArray = (map: Map<string, any>, keyName: string) => {
            return Array.from(map.entries())
                .map(([key, val]) => ({ [keyName]: key, ...val }))
                .sort((a, b) => b.clicks - a.clicks);
        };

        // Active Campaigns
        const activeCampaigns = Array.from(campaignsMap.values()).map(c => ({
            campaign: c.name,
            impressions: c.impressions,
            clicks: c.clicks,
            ctr: c.impressions > 0 ? ((c.clicks / c.impressions) * 100).toFixed(2) + '%' : '0%'
        })).sort((a, b) => b.impressions - a.impressions);
        this.activeCampaigns.set(activeCampaigns);

        // Breakdowns
        // Round `cost` to 2 decimal places here (page-local) so the Top Cities
        // table renders clean values like 37322.71 instead of 37322.70803100002,
        // without modifying the shared scroll-table component.
        this.topCities.set(toSortedArray(citiesMap, 'city').map(c => ({
            city: c.city,
            clicks: c.clicks,
            // cost: c.spend,
            cost: Math.round((Number(c.spend) || 0) * 100) / 100
        })));
        this.devices.set(toSortedArray(devicesMap, 'device'));
        this.genders.set(toSortedArray(gendersMap, 'gender'));
        this.ages.set(toSortedArray(agesMap, 'age'));
        this.platforms.set(toSortedArray(platformsMap, 'platform'));

        // Global Cards Calculation
        const globalCtr = globalStats.impressions > 0 ? (globalStats.clicks / globalStats.impressions) * 100 : 0;
        const globalCpc = globalStats.clicks > 0 ? (globalStats.spend / globalStats.clicks) : 0;
        const globalCpm = globalStats.impressions > 0 ? (globalStats.spend / globalStats.impressions) * 1000 : 0;

        this.adsStats.set({
            impressions: { value: globalStats.impressions, change: 0 },
            clicks: { value: globalStats.clicks, change: 0 },
            spend: { value: globalStats.spend, change: 0 },
            ctr: { value: globalCtr, change: 0 },
            avgCpc: { value: globalCpc, change: 0 },
            cpm: { value: globalCpm, change: 0 },
            reach: { value: globalStats.reach, change: 0 }
        });
    }

    private resetSignals() {
        this.activeCampaigns.set([]);
        this.topCities.set([]);
        this.devices.set([]);
        this.genders.set([]);
        this.ages.set([]);
        this.platforms.set([]);
        this.adsStats.set({
            impressions: { value: 0, change: 0 },
            clicks: { value: 0, change: 0 },
            avgCpc: { value: 0, change: 0 },
            spend: { value: 0, change: 0 },
            reach: { value: 0, change: 0 },
            cpm: { value: 0, change: 0 },
            ctr: { value: 0, change: 0 }
        });
    }
}